import { weakStateToString } from '@cck/backend/dist/evaluation/EvaluationUtils'
import { getAll as LoadStaffs } from '@cck/backend/dist/rcm/StaffManager'
import {
  DesignWeakState,
  ItemState,
  OperationWeakState,
  ProgressState,
  ResultState,
  Type as EvaluationType,
  DesignDefect,
  OperationDefect,
  isOperationDefect,
  EvaluationItem,
  isAbleToDefect
} from '@cck/common/dist/data/Evaluation'
import { Staff } from '@cck/common/dist/data/Staff'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Button from 'antd/lib/button'
import Modal from 'antd/lib/modal'
import Typography from 'antd/lib/typography'
import clsx from 'clsx'
import _ from 'lodash'
import React from 'react'
import useResizeAware from 'react-resize-aware'

import { grey } from '../../../base/color'
import TableHeader from '../../common/TableHeader'
import TableItem from '../../common/TableItem'
import DesignEvaluationDefectView from './DesignEvaluationDefectView'
import OperationEvaluationDefectView from './OperationEvaluationDefectView'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    border: grey.border,
    margin: theme.spacing(2)
  },
  warningText: {
    marginRight: theme.spacing(2)
  },
  invisibleWarningText: {
    visibility: 'hidden'
  },
  stringText: {
    whiteSpace: 'break-spaces'
  },
  table: {
    padding: theme.spacing(2)
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  }
}))

interface Props {
  type: EvaluationType
  evaluation: EvaluationItem
  evaluationDefect: ItemState
  onSaveEvaluationDefect: (
    weakState: OperationWeakState | DesignWeakState,
    weakStateData: OperationDefect | DesignDefect
  ) => Promise<void>
}

const EvaluationDefectSummary: React.FC<Props> = ({
  type,
  evaluation,
  evaluationDefect,
  onSaveEvaluationDefect
}) => {
  const classes = useStyles()
  const [items, setItems] = React.useState(
    [] as { name: string; value: React.ReactNode; noDivider?: boolean }[]
  )
  const [states, setStates] = React.useState({
    visible: false
  })
  const [staffs, setStaffs] = React.useState<Staff[]>([])
  const [resizeListener, sizes] = useResizeAware()

  React.useEffect(() => {
    LoadStaffs().then((newStaffs) => {
      setStaffs(newStaffs)
    })
  }, [])

  React.useEffect(() => {
    const newItems: { name: string; value: React.ReactNode; noDivider?: boolean }[] = []
    let weakState = evaluationDefect.weakState
    if (type === EvaluationType.Operation && evaluation.state.state !== ProgressState.Approved) {
      weakState = OperationWeakState.Default
    }

    newItems.push({
      name: '상태',
      value: weakStateToString(type, weakState)
    })

    const weakStateData = evaluationDefect.weakStateData
    if (type === EvaluationType.Operation && isOperationDefect(weakStateData)) {
      _.forEach(['1', '2', '3', '4', '5'], (key: '1' | '2' | '3' | '4' | '5') => {
        if (_.isEmpty(weakStateData[key].answer)) {
          return
        }
        newItems.push({
          name: `${key}. 질문/답변`,
          value: (
            <span>
              {weakStateData[key].question}
              <span style={{ marginLeft: 12, fontWeight: 'bold' }}>
                {weakStateData[key].answer}
              </span>
            </span>
          )
        })
        newItems.push({
          name: `${key}. 근거`,
          value: weakStateData[key].reason
        })
      })
    }

    const last = _.last(newItems)
    if (last) {
      last.noDivider = true
    }
    setItems(newItems)
  }, [evaluationDefect])

  return (
    <div>
      {resizeListener}
      <div className={classes.button}>
        <Typography.Text
          className={clsx(classes.warningText, {
            [classes.invisibleWarningText]: isAbleToDefect(evaluation)
          })}
          strong
          type="danger"
        >
          {type === EvaluationType.Operation &&
            evaluation.state.state !== ProgressState.Approved &&
            '승인 완료 이후에 미비점 평가를 진행할 수 있습니다.'}
          {evaluation.state.resultState === ResultState.Normal && '문제가 없는 통제입니다.'}
        </Typography.Text>
        {type === EvaluationType.Operation && (
          <Button
            type="primary"
            disabled={!isAbleToDefect(evaluation)}
            onClick={() => setStates({ ...states, visible: true })}
          >
            미비점 평가
          </Button>
        )}
      </div>
      <div className={classes.root}>
        <TableHeader value="미비점 평가" />
        {_.map(items, (item) => {
          return (
            <TableItem key={item.name} name={item.name} noDivider={item.noDivider}>
              {item.value}
            </TableItem>
          )
        })}
      </div>
      <Modal
        bodyStyle={{ padding: 0 }}
        footer={
          type === 'operation'
            ? [
                <Button key="back" onClick={() => setStates({ ...states, visible: false })}>
                  닫기
                </Button>
              ]
            : []
        }
        maskClosable={false}
        title="미비점 평가 수행"
        visible={states.visible}
        width={sizes.width ? sizes.width * 0.8 : 1000}
        onCancel={() => setStates({ ...states, visible: false })}
        onOk={() => setStates({ ...states, visible: false })}
      >
        {type === 'operation' && (
          <OperationEvaluationDefectView
            onSaveEvaluationDefect={(weakState, newEvaluationDefect) => {
              return onSaveEvaluationDefect(weakState, newEvaluationDefect).then(() => {
                setStates({ ...states, visible: false })
              })
            }}
          />
        )}
        {type === 'design' && (
          <DesignEvaluationDefectView
            editable
            evaluation={evaluation}
            itemState={evaluationDefect}
            staffs={staffs}
            onSaveEvaluationDefect={(weakState, newEvaluationDefect) => {
              return onSaveEvaluationDefect(weakState, newEvaluationDefect).then(() => {
                setStates({ ...states, visible: false })
              })
            }}
          />
        )}
      </Modal>
    </div>
  )
}

export default EvaluationDefectSummary
